"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import MessageCircle from "lucide-react/dist/esm/icons/message-circle";
import Loader2 from "lucide-react/dist/esm/icons/loader";
import { toast } from "sonner";
import { Button, type ButtonProps } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useProfile } from "@/contexts/ProfileContext";
import { useSession } from "@/contexts/SessionContext";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { useStartConversation } from "@/hooks/useMessages";
import { humanizeError } from "@/lib/error-humanizer";
import { MESSAGE_MAX_LENGTH, PAYMENT_WARNING, STAFF_ACCESS_NOTICE, type ContextType } from "@/lib/messages";
import { openChatDock } from "./ChatDock";
import { ResponseTimeBadge } from "./ChatBits";

/**
 * "Message seller" — the entry point on stores, assets, creator cards and jobs.
 * A first message goes in a small dialog (an empty thread helps nobody), then
 * the conversation opens in the dock on desktop or /messages on a phone.
 * Signed-out visitors get the sign-up gate first.
 */
export function MessageSellerButton({
  recipientId,
  recipientName,
  contextType,
  contextId,
  contextLabel,
  label = "Message",
  placeholder,
  variant = "outline",
  size = "default",
  className,
  showIcon = true,
}: {
  recipientId: string;
  recipientName: string;
  contextType?: ContextType;
  contextId?: string | null;
  /** Shown in the dialog: "About <label>". */
  contextLabel?: string | null;
  label?: string;
  placeholder?: string;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  className?: string;
  showIcon?: boolean;
}) {
  const router = useRouter();
  const { user } = useSession();
  const { profile } = useProfile();
  const { openAuthModal } = useAuthModal();
  const start = useStartConversation();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");

  if (profile?.id === recipientId) return null;

  const onClick = () => {
    if (!user) {
      openAuthModal({ onSuccess: () => setOpen(true) });
      return;
    }
    setOpen(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    try {
      const res = await start.mutateAsync({ recipientId, body, contextType, contextId: contextId ?? null });
      setBody("");
      setOpen(false);
      if (res.payment_warning) {
        toast.warning("Message sent — one thing first", { description: PAYMENT_WARNING, duration: 12_000 });
      } else {
        toast.success(`Message sent to ${recipientName}`);
      }
      openChatDock({ conversationId: res.conversation_id }, router);
    } catch (err) {
      toast.error(humanizeError(err, "sending the message"));
    }
  };

  return (
    <>
      <Button type="button" variant={variant} size={size} className={className} onClick={onClick}>
        {showIcon && <MessageCircle className="mr-1.5 h-4 w-4" aria-hidden />}
        {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Message {recipientName}</DialogTitle>
            <DialogDescription>
              {contextLabel ? <>About <span className="font-medium text-foreground">{contextLabel}</span>. </> : null}
              Ask anything before you buy or hire — they'll get it right away.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={MESSAGE_MAX_LENGTH}
              className="min-h-[120px]"
              placeholder={placeholder ?? `Hi ${recipientName}, …`}
              autoFocus
              aria-label="Your message"
            />
            <ResponseTimeBadge profileId={recipientId} />
            <p className="text-2xs text-muted-foreground">{STAFF_ACCESS_NOTICE}</p>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!body.trim() || start.isPending}>
                {start.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />}
                Send
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
